/**
 * Security 24h summary widget — sits above the sub-tabs on the System
 * Admin → Security tab. At-a-glance numbers for "anything blow up
 * overnight?" Each tile links to the relevant sub-tab.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, ShieldWarning, Lock, Warning, CheckCircle } from "@phosphor-icons/react";

function Tile({ label, value, intent = "neutral", subtext, onClick, testid }) {
  const colors = {
    neutral: { bg: "#FAFAFA",  border: "#E5E5E5", value: "#0A0A0A", chip: "#737373" },
    info:    { bg: "#F0F7FF",  border: "#BFDBFE", value: "#1E40AF", chip: "#1E40AF" },
    warning: { bg: "#FFFBEB",  border: "#FDE68A", value: "#92400E", chip: "#92400E" },
    danger:  { bg: "#FEE2E2",  border: "#FCA5A5", value: "#991B1B", chip: "#DC2626" },
    success: { bg: "#F0FDF4",  border: "#BBF7D0", value: "#166534", chip: "#166534" },
  }[intent] || {};
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="flex flex-col text-left p-4 border transition-colors hover:border-[#0A0A0A] disabled:cursor-default"
      style={{ background: colors.bg, borderColor: colors.border }}
      data-testid={testid}
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: colors.chip }}>
        {label}
      </span>
      <span className="text-3xl font-black tracking-tighter mt-1" style={{ color: colors.value }}>
        {value}
      </span>
      {subtext && (
        <span className="text-[10px] mt-0.5" style={{ color: colors.chip }}>{subtext}</span>
      )}
    </button>
  );
}

export default function SecuritySummaryWidget({ onJumpTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/security/summary", { params: { hours } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load security summary");
    }
    setLoading(false);
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return (
    <div className="p-4 border border-[#E5E5E5] text-xs text-[#A3A3A3]">Loading security summary…</div>
  );
  if (!data) return null;

  const delta = data.failed_attempts_delta_pct;
  const deltaIntent = delta == null ? "neutral" : delta >= 100 ? "danger" : delta >= 25 ? "warning" : "neutral";
  const failedIntent = data.failed_attempts > 50 ? "danger" : data.failed_attempts > 5 ? "warning" : "neutral";

  return (
    <div className="space-y-3" data-testid="security-summary-widget">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center gap-1.5">
            <ShieldWarning size={14} />Security overview
          </h2>
          <p className="text-xs text-[#525252] mt-0.5">
            At-a-glance numbers for the last <strong>{data.hours}h</strong>. Click any tile to drill in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={hours} onChange={e => setHours(Number(e.target.value))}
                  className="h-8 px-2 text-xs border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                  data-testid="security-summary-window">
            <option value={1}>1 hour</option>
            <option value={6}>6 hours</option>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
          </select>
          <button onClick={load} disabled={loading}
                  className="h-8 px-2 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] flex items-center gap-1"
                  data-testid="security-summary-refresh">
            <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <Tile
          label="Failed logins"
          value={data.failed_attempts}
          intent={failedIntent}
          subtext={
            delta == null
              ? "no comparable window yet"
              : delta === 0
              ? "no change vs previous window"
              : `${delta > 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs prev ${data.hours}h`
          }
          onClick={() => onJumpTo?.("failed")}
          testid="security-tile-failed"
        />
        <Tile
          label="Successful logins"
          value={data.successful_logins}
          intent="success"
          subtext={`vs ${data.failed_attempts} failed`}
          testid="security-tile-success"
        />
        <Tile
          label="Soft-locked"
          value={data.soft_locked_accounts}
          intent={data.soft_locked_accounts > 0 ? "warning" : "neutral"}
          subtext="rate-limit timeouts"
          onClick={() => onJumpTo?.("locked")}
          testid="security-tile-soft-locked"
        />
        <Tile
          label="Hard-locked"
          value={data.hard_locked_accounts}
          intent={data.hard_locked_accounts > 0 ? "danger" : "neutral"}
          subtext="admin unlock required"
          onClick={() => onJumpTo?.("locked")}
          testid="security-tile-hard-locked"
        />
        <Tile
          label="CSP violations"
          value={data.csp_violations}
          intent={data.csp_violations > 0 ? "warning" : "neutral"}
          subtext="browser-reported"
          onClick={() => onJumpTo?.("csp")}
          testid="security-tile-csp"
        />
      </div>
      {data.top_offenders?.length > 0 && (
        <div className="text-[11px] text-[#525252] bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Warning size={12} className="text-[#D97706]" weight="fill" />
          <span className="font-bold uppercase tracking-wider text-[10px] text-[#525252]">Top offenders</span>
          {data.top_offenders.map((o, i) => (
            <span key={i} className="text-[#0A0A0A]" data-testid={`security-top-offender-${i}`}>
              <strong>{o.email}</strong> <span className="text-[#737373]">×{o.n}</span>
              {i < data.top_offenders.length - 1 && <span className="text-[#A3A3A3] ml-3">·</span>}
            </span>
          ))}
        </div>
      )}
      {data.failed_attempts === 0 && data.hard_locked_accounts === 0 && data.csp_violations === 0 && (
        <div className="text-[11px] text-[#166534] bg-[#F0FDF4] border border-[#BBF7D0] px-3 py-2 flex items-center gap-2">
          <CheckCircle size={12} weight="fill" />
          <span>Nothing notable in the last {data.hours}h.</span>
        </div>
      )}
    </div>
  );
}
